// Helpers purs de la section « Prix vs marché » : mise en forme des écarts et
// des prix, couleur d'une tranche, filtres et tri de la liste des produits.
// Les composants React ne gardent que le rendu et l'appel réseau.
//
// Contrat back_api : GET /price-benchmarks?lang=fr|es →
//   { market, updatedAt, total, medianRatio,
//     bands: [{label, count, share}],
//     brands: [{name, count, medianRatio, cheaperShare}],
//     categories: [...], products: [{slug, title, brand, category, price, benchmarkPrice, ratio, label}] }
// Toute liste peut manquer : les helpers renvoient toujours une valeur affichable.

// Tranches, de la plus favorable à la moins favorable (mêmes libellés que le
// custom_label_2 du flux CSS).
export const BAND_ORDER = ["Prix -10%", "Prix -3%", "Prix marché", "Prix +3%", "Prix +10%"];

// Couleur Bootstrap d'une tranche.
export function bandTone(label) {
    switch (label) {
        case "Prix -10%":
            return "success";
        case "Prix -3%":
            return "info";
        case "Prix marché":
            return "secondary";
        case "Prix +3%":
            return "warning";
        case "Prix +10%":
            return "danger";
        default:
            return "light";
    }
}

// Écart relatif → « −13,3 % » (signe explicite, virgule française).
export function formatRatio(ratio, digits = 1) {
    const value = Number(ratio);
    if (!Number.isFinite(value)) return "—";
    const pct = (value * 100).toFixed(digits).replace(".", ",");
    if (value > 0) return "+" + pct + " %";
    if (value < 0) return "−" + pct.replace("-", "") + " %";
    return pct + " %";
}

// Part 0–1 → « 79 % ».
export function formatShare(share) {
    const value = Number(share);
    if (!Number.isFinite(value)) return "—";
    return Math.round(value * 100) + " %";
}

export function formatEur(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toFixed(2).replace(".", ",") + " €";
}

export function formatUpdatedAt(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Valeurs distinctes d'un champ, triées (pour les sélecteurs).
export function distinctValues(products, field) {
    const set = new Set();
    (Array.isArray(products) ? products : []).forEach((p) => {
        if (p && p[field]) set.add(String(p[field]));
    });
    return [...set].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
}

// Filtre : marque, catégorie, tranche et recherche texte (titre ou slug).
export function filterProducts(products, { brand = "", category = "", band = "", query = "" } = {}) {
    const q = String(query || "").trim().toLowerCase();
    return (Array.isArray(products) ? products : []).filter((p) => {
        if (!p) return false;
        if (brand && p.brand !== brand) return false;
        if (category && p.category !== category) return false;
        if (band && p.label !== band) return false;
        if (q && !(String(p.title || "").toLowerCase().includes(q) || String(p.slug || "").toLowerCase().includes(q))) return false;
        return true;
    });
}

// Tri stable par colonne ; `dir` = "asc" | "desc".
export function sortProducts(products, key = "ratio", dir = "asc") {
    const sign = dir === "desc" ? -1 : 1;
    return [...(Array.isArray(products) ? products : [])].sort((a, b) => {
        const va = a ? a[key] : undefined;
        const vb = b ? b[key] : undefined;
        if (typeof va === "number" && typeof vb === "number") return sign * (va - vb);
        return sign * String(va || "").localeCompare(String(vb || ""), "fr", { sensitivity: "base" });
    });
}

// Part des produits au moins 3 % moins chers que le marché.
export function cheaperShare(bands) {
    const list = Array.isArray(bands) ? bands : [];
    const total = list.reduce((s, b) => s + (Number(b.count) || 0), 0);
    if (!total) return 0;
    const cheaper = list.filter((b) => b.label === "Prix -10%" || b.label === "Prix -3%").reduce((s, b) => s + (Number(b.count) || 0), 0);
    return cheaper / total;
}
